import {
  Commitment,
  CommitmentTimelineActivityCategory,
  ReallocationEventDTO,
} from "../types/Commitment";

export type ReallocationActivityCategory = Extract<
  CommitmentTimelineActivityCategory,
  | "REALLOCATION_STARTED"
  | "REALLOCATION_CONFIRMED"
  | "REALLOCATION_SENT"
  | "REALLOCATION_CANCELLED"
  | "REALLOCATION_RECEIVED"
>;

export type ResolvedReallocationCommitments = {
  source: Commitment | undefined;
  destination: Commitment | undefined;
  isPersonalToIsaWrap: boolean;
};

type ResolveReallocationCommitmentsArgs = {
  event: ReallocationEventDTO;
  commitmentId: string;
  commitments: Commitment[];
};

export const resolveReallocationCommitments = ({
  event,
  commitmentId,
  commitments,
}: ResolveReallocationCommitmentsArgs): ResolvedReallocationCommitments => {
  const current = commitments.find((c) => c.id === commitmentId);
  const other = commitments.find(
    (c) =>
      c.dealName === event.dealName &&
      c.entityId === current?.entityId &&
      c.id !== commitmentId
  );

  const isReceived = event.activityCategory === "REALLOCATION_RECEIVED";
  const source = isReceived ? other : current;
  const destination = isReceived ? current : other;

  const sourceIsPersonal =
    !!source && source.entityId == null && source.ifIsaAccountId == null;
  const destinationIsIsa = !!destination && destination.ifIsaAccountId != null;

  return {
    source,
    destination,
    isPersonalToIsaWrap: sourceIsPersonal && destinationIsIsa,
  };
};

export type ReallocationDescriptionArgs = {
  dealName: string;
};

export type ReallocationCopyVariant = {
  titleByCategory: Record<ReallocationActivityCategory, string>;
  descriptionByCategory: Partial<
    Record<
      ReallocationActivityCategory,
      (args: ReallocationDescriptionArgs) => string
    >
  >;
  fromLabelByCategory: Record<ReallocationActivityCategory, string>;
  secondPointLabelByCategory: Partial<
    Record<ReallocationActivityCategory, string>
  >;
  startedCardText: string;
  awaitingTitle: string;
  awaitingDescription: string;
  receivedCardText: string;
};

export const REALLOCATION_COPY_STANDARD: ReallocationCopyVariant = {
  titleByCategory: {
    REALLOCATION_STARTED: "Reallocation Started",
    REALLOCATION_CONFIRMED: "Reallocation Confirmed",
    REALLOCATION_SENT: "Reallocation Confirmed",
    REALLOCATION_CANCELLED: "Reallocation Cancelled",
    REALLOCATION_RECEIVED: "Reallocation Received",
  },
  descriptionByCategory: {},
  fromLabelByCategory: {
    REALLOCATION_STARTED: "YOUR FUNDS WILL BE REALLOCATED FROM:",
    REALLOCATION_CONFIRMED: "YOUR FUNDS WERE REALLOCATED FROM:",
    REALLOCATION_SENT: "YOUR FUNDS WERE REALLOCATED FROM:",
    REALLOCATION_CANCELLED: "FUNDS WERE TO BE REALLOCATED FROM:",
    REALLOCATION_RECEIVED: "FUNDS REALLOCATED FROM:",
  },
  secondPointLabelByCategory: {
    REALLOCATION_CONFIRMED: "Reallocation Confirmed",
    REALLOCATION_SENT: "Reallocation Confirmed",
    REALLOCATION_CANCELLED: "Reallocation Cancelled",
  },
  startedCardText: "Reallocation Started",
  awaitingTitle: "Awaiting Reallocation",
  awaitingDescription: "Your reallocation request is pending.",
  receivedCardText: "Reallocation Received",
};

export const REALLOCATION_COPY_ISA_WRAP: ReallocationCopyVariant = {
  titleByCategory: {
    REALLOCATION_STARTED: "ISA move started",
    REALLOCATION_CONFIRMED: "Move to ISA Completed",
    REALLOCATION_SENT: "Move to ISA Confirmed",
    REALLOCATION_CANCELLED: "Move to ISA Cancelled",
    REALLOCATION_RECEIVED: "Move to ISA Received",
  },
  descriptionByCategory: {
    REALLOCATION_STARTED: ({ dealName }) =>
      `Funds reserved for the IF-ISA account for ${dealName}.`,
  },
  fromLabelByCategory: {
    REALLOCATION_STARTED: "YOUR PERSONAL FUNDS WILL BE MOVED",
    REALLOCATION_CONFIRMED: "YOUR PERSONAL FUNDS WERE MOVED TO YOUR ISA",
    REALLOCATION_SENT: "YOUR PERSONAL FUNDS WERE MOVED TO YOUR ISA",
    REALLOCATION_CANCELLED: "FUNDS WERE TO BE MOVED TO YOUR ISA",
    REALLOCATION_RECEIVED: "FUNDS MOVED TO YOUR ISA",
  },
  secondPointLabelByCategory: {
    REALLOCATION_CONFIRMED: "Move to ISA Completed",
    REALLOCATION_SENT: "Move to ISA Confirmed",
    REALLOCATION_CANCELLED: "Move to ISA Cancelled",
  },
  startedCardText: "Move to ISA Started",
  awaitingTitle: "Awaiting Move",
  awaitingDescription:
    "Your move to ISA is pending. We usually process ISA moves within 5 working days. You will receive an email when the ISA move is complete.",
  receivedCardText: "Move to ISA Received",
};

export const getReallocationCopy = (
  isPersonalToIsaWrap: boolean
): ReallocationCopyVariant =>
  isPersonalToIsaWrap ? REALLOCATION_COPY_ISA_WRAP : REALLOCATION_COPY_STANDARD;
